import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { RefreshCw, ReceiptText } from "lucide-react";
import { toast } from "react-toastify";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";

// ── Property Helpers for Desktop Invoices ──
const getInvoiceKey = (item) =>
  item?.qbId ||
  item?.meta?.invoiceId ||
  item?.meta?.estimateId ||
  item?.sourceId ||
  item?.TxnID ||
  item?.raw?.TxnID ||
  item?._id;

const getInvoiceDocNumber = (item) => {
  if (item?.invoiceNumber && item.invoiceNumber.trim()) return item.invoiceNumber;
  if (item?.docNumber && item.docNumber.trim()) return item.docNumber;
  if (item?.RefNumber && item.RefNumber.trim()) return item.RefNumber;
  if (item?.raw?.RefNumber && item.raw.RefNumber.trim()) return item.raw.RefNumber;
  if (item?.raw?.TxnNumber) return item.raw.TxnNumber;
  if (item?.qbId) return item.qbId;
  if (item?.meta?.invoiceId) return item.meta.invoiceId;
  if (item?.message) {
    const match = item.message.match(/Invoice\s+(\w+)/i);
    if (match && match[1]) return match[1];
  }
  return "—";
};

const getInvoiceCustomerName = (item) =>
  item?.customerName ||
  item?.contact?.fullName ||
  item?.CustomerRef?.FullName ||
  item?.raw?.CustomerRef?.FullName ||
  item?.sendBy?.fullName ||
  item?.senderDetails?.fullName ||
  item?.sendTo?.fullName ||
  item?.senderEmail ||
  "—";

const getInvoiceCreatedDate = (item) =>
  item?.createdAtQb ||
  item?.txnDate ||
  item?.TxnDate ||
  item?.raw?.TxnDate ||
  item?.raw?.TimeCreated ||
  item?.createdAt;

const getInvoiceDueDate = (item) =>
  item?.dueDate ||
  item?.DueDate ||
  item?.raw?.DueDate ||
  item?.createdAt;

const getInvoiceShipDate = (item) =>
  item?.shipDate ||
  item?.raw?.ShipDate ||
  item?.dueDate;

const getInvoiceSubtotal = (item) =>
  item?.subtotal ??
  item?.subtotalaftermarkup ??
  item?.customer_subtotal ??
  item?.totalAmount ??
  item?.meta?.amount ??
  item?.meta?.totalAmount ??
  item?.Subtotal ??
  item?.TotalAmount ??
  item?.raw?.Subtotal ??
  0;

const getInvoiceTotalTax = (item) =>
  Number(
    item?.totalTax ??
      item?.raw?.SalesTaxTotal ??
      item?.taxDetails?.totalTax ??
      0
  );

const getInvoiceAmountDue = (item) =>
  Number(
    item?.totalAmountDue ??
      item?.remainingAmount ??
      item?.balanceRemaining ??
      item?.BalanceRemaining ??
      item?.meta?.remainingBalance ??
      item?.AmountDue ??
      item?.raw?.BalanceRemaining ??
      0
  );

const getInvoiceType = (item) =>
  item?.invoiceType || "full";

const getInvoicePaymentStatus = (item) => {
  const s = String(
    item?.paymentStatus || item?.status || item?.meta?.paymentStatus || ""
  ).toLowerCase();
  if (s === "paid" || getInvoiceAmountDue(item) === 0) return "paid";
  if (s === "partial" || item?.partialPaid) return "partial";
  return "unpaid";
};

const getInvoiceIsPaid = (item) => getInvoicePaymentStatus(item) === "paid";

const getInvoiceLines = (item) =>
  item?.originalLineItems?.length
    ? item.originalLineItems
    : item?.markupLineItems?.length
    ? item.markupLineItems
    : item?.lines?.length
    ? item.lines
    : item?.InvoiceLineRet?.length
    ? item.InvoiceLineRet
    : item?.Line?.length
    ? item.Line
    : item?.raw?.InvoiceLineRet?.length
    ? item.raw.InvoiceLineRet
    : item?.raw?.Line?.length
    ? item.raw.Line
    : [];

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatAddress = (addr) => {
  if (!addr) return null;
  if (typeof addr === "string") return addr;
  const parts = [
    addr.Addr1 || addr.Line1,
    addr.Addr2 || addr.Line2,
    addr.City || addr.city,
    addr.State || addr.state,
    addr.PostalCode || addr.postalCode,
    addr.Country || addr.country,
  ].filter(Boolean);
  return parts.join(", ") || null;
};

const StatusBadge = ({ status, amtDue }) => {
  const s = String(status || "").toLowerCase();
  const isPaid = s === "paid" || Number(amtDue) === 0;
  const isPartial = s === "partial";

  if (isPaid) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#F0FEF4] text-[#008236] border border-[#0a7822a6] capitalize">
        <span className="size-1.5 rounded-full bg-[#0a7822a6]" />
        Paid
      </span>
    );
  }
  if (isPartial) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 capitalize">
        <span className="size-1.5 rounded-full bg-blue-500" />
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 capitalize">
      <span className="size-1.5 rounded-full bg-amber-500" />
      Unpaid
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const isFull = String(type || "full").toLowerCase() === "full";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium capitalize border ${
        isFull
          ? "bg-purple-50 text-purple-700 border-purple-200"
          : "bg-blue-50 text-blue-700 border-blue-200"
      }`}
    >
      {isFull ? "Full" : "Partial"}
    </span>
  );
};

const StatCard = ({ label, value, bg, icon }) => (
  <div className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4">
    <div className={`size-12 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
      {icon}
    </div>
    <div>
      <p className="text-sm text-gray-600 font-medium">{label}</p>
      <p className="text-xl font-semibold text-gray-900 mt-0.5">{value}</p>
    </div>
  </div>
);

export default function QBDInvoiceList() {
  const history = useNavigate();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "unpaid" | "paid" | "partial"
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [isSyncing, setIsSyncing] = useState(false);

  const fetchInvoices = async () => {
    loader(true);
    await ApiClient.get(`qb-desktop/list/invoices?companyId=${companyId}`)
      .then((res) => {
        if (res?.success) setInvoices(res?.data || []);
      })
      .catch(console.error);
    loader(false);
  };

  const handleSyncInvoices = async () => {
    try {
      setIsSyncing(true);
      loader(true);
      const res = await ApiClient.get("qb-desktop/sync/invoices");
      if (res?.success || res?.status) {
        toast.success("Invoice sync job queued for Web Connector!");
        fetchInvoices();
      } else {
        toast.error(res?.message || "Failed to queue invoice sync.");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to queue invoice sync.");
    } finally {
      setIsSyncing(false);
      loader(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const filteredInvoices = invoices.filter((inv) => {
    const q = searchQuery.toLowerCase();
    const custName = getInvoiceCustomerName(inv).toLowerCase();
    const docNum = String(getInvoiceDocNumber(inv)).toLowerCase();
    const qbId = String(getInvoiceKey(inv)).toLowerCase();
    const title = String(inv?.title || "").toLowerCase();
    const msg = String(inv?.message || "").toLowerCase();

    const matchesSearch =
      !q ||
      custName.includes(q) ||
      docNum.includes(q) ||
      qbId.includes(q) ||
      title.includes(q) ||
      msg.includes(q);

    const st = getInvoicePaymentStatus(inv);
    const matchesStatus =
      statusFilter === ""
        ? true
        : statusFilter === "paid"
        ? st === "paid"
        : statusFilter === "partial"
        ? st === "partial"
        : st === "unpaid";

    return matchesSearch && matchesStatus;
  });

  const totalInvoices = invoices.length;
  const totalDue = invoices.reduce(
    (acc, inv) => acc + getInvoiceAmountDue(inv),
    0
  );
  const paidCount = invoices.filter(
    (inv) => getInvoicePaymentStatus(inv) === "paid"
  ).length;

  const closeModal = () => {
    setIsViewModalOpen(false);
    setSelectedInvoice(null);
  };

  const STATUS_TABS = [
    { label: "All", value: "" },
    { label: "Unpaid", value: "unpaid" },
    { label: "Paid", value: "paid" },
    { label: "Partial", value: "partial" },
  ];

  const getInvoiceTabCount = (tabVal) => {
    if (!tabVal) return invoices.length;
    if (tabVal === "paid")
      return invoices.filter((inv) => getInvoicePaymentStatus(inv) === "paid").length;
    if (tabVal === "partial")
      return invoices.filter((inv) => getInvoicePaymentStatus(inv) === "partial").length;
    return invoices.filter((inv) => getInvoicePaymentStatus(inv) === "unpaid").length;
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              QB Desktop – Invoices
            </h2>
            <p className="text-sm text-gray-600">
              {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              type="button"
              disabled={isSyncing}
              onClick={handleSyncInvoices}
              className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-[#115B9D] text-gray-700 hover:text-[#115B9D] text-sm rounded-xl transition-all font-medium px-4 py-2.5 shadow-xs disabled:opacity-60"
            >
              <RefreshCw className={`size-4 text-[#115B9D] ${isSyncing ? "animate-spin" : ""}`} />
              <span>Sync Invoices</span>
            </button>
            <button
              type="button"
              onClick={() => history("/qb-desktop/invoices/add")}
              className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
              Create Invoice
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            label="Total Invoices"
            value={totalInvoices}
            bg="bg-[#115B9D17]"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#115B9D]"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            }
          />
          <StatCard
            label="Total Amount Due"
            value={formatCurrency(totalDue)}
            bg="bg-amber-50"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-amber-600"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            }
          />
          <StatCard
            label="Paid Invoices"
            value={paidCount}
            bg="bg-[#F0FEF4]"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#008236]"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            }
          />
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* Filters & Search */}
          <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
            <div className="max-sm:w-full w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
              {STATUS_TABS.map((tab) => {
                const count = getInvoiceTabCount(tab.value);
                const isActive = statusFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isActive
                        ? "bg-white text-gray-900 shadow-sm font-semibold"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        isActive
                          ? "bg-[#115B9D]/10 text-[#115B9D] font-bold"
                          : "bg-gray-200/70 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="relative w-full sm:w-[320px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by customer, invoice #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* Desktop Table (Matching Online QuickBooks Invoice Listing) */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "5%" }} /> {/* Actions */}
                <col style={{ width: "12%" }} /> {/* Invoice */}
                <col style={{ width: "18%" }} /> {/* Customer */}
                <col style={{ width: "8%" }} /> {/* Type */}
                <col style={{ width: "10%" }} /> {/* Created */}
                <col style={{ width: "10%" }} /> {/* Due Date */}
                <col style={{ width: "10%" }} /> {/* Ship Date */}
                <col style={{ width: "8%" }} /> {/* Tax */}
                <col style={{ width: "11%" }} /> {/* Amount Due */}
                <col style={{ width: "8%" }} /> {/* Status */}
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Invoice",
                    "Customer",
                    "Type",
                    "Created",
                    "Due Date",
                    "Ship Date",
                    "Tax",
                    "Amount Due",
                    "Status",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-2">
                      <span className="text-xs text-gray-700 font-medium block truncate">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">
                          No invoices found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((item, index) => {
                    const invKey = getInvoiceKey(item) || index;
                    const docNum = getInvoiceDocNumber(item);
                    const custName = getInvoiceCustomerName(item);
                    const createdDate = getInvoiceCreatedDate(item);
                    const dueDate = getInvoiceDueDate(item);
                    const shipDate = getInvoiceShipDate(item);
                    const taxAmt = getInvoiceTotalTax(item);
                    const amtDue = getInvoiceAmountDue(item);
                    const invType = getInvoiceType(item);
                    const st = getInvoicePaymentStatus(item);

                    return (
                      <tr
                        key={invKey}
                        className="hover:bg-gray-50/60 transition-colors align-middle"
                      >
                        {/* 1. Actions */}
                        <td className="py-2.5 px-2 align-middle">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedInvoice(item);
                              setIsViewModalOpen(true);
                            }}
                            title="View Details"
                            className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </td>
                        {/* 2. Invoice */}
                        <td className="py-2.5 px-2 align-middle overflow-hidden">
                          <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D] truncate max-w-full">
                            {docNum !== "—" ? `#${docNum}` : "—"}
                          </span>
                        </td>
                        {/* 3. Customer */}
                        <td className="py-2.5 px-2 align-middle overflow-hidden">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="size-7 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                              <span className="text-[9px] font-semibold text-[#115B9D]">
                                {getInitials(custName)}
                              </span>
                            </div>
                            <p className="text-xs font-medium text-gray-800 truncate">
                              {custName}
                            </p>
                          </div>
                        </td>
                        {/* 4. Type */}
                        <td className="py-2.5 px-2 align-middle">
                          <TypeBadge type={invType} />
                        </td>
                        {/* 5. Created */}
                        <td className="py-2.5 px-2 align-middle text-xs text-gray-600">
                          {formatDate(createdDate)}
                        </td>
                        {/* 6. Due Date */}
                        <td className="py-2.5 px-2 align-middle text-xs text-gray-600">
                          {formatDate(dueDate)}
                        </td>
                        {/* 7. Ship Date */}
                        <td className="py-2.5 px-2 align-middle text-xs text-gray-600">
                          {formatDate(shipDate)}
                        </td>
                        {/* 8. Tax */}
                        <td className="py-2.5 px-2 align-middle text-xs text-gray-700 font-medium">
                          {formatCurrency(taxAmt)}
                        </td>
                        {/* 9. Amount Due */}
                        <td className="py-2.5 px-2 align-middle">
                          <p
                            className={`text-xs font-semibold whitespace-nowrap ${
                              st === "paid"
                                ? "text-[#008236]"
                                : st === "partial"
                                ? "text-blue-600"
                                : "text-amber-600"
                            }`}
                          >
                            {formatCurrency(amtDue)}
                          </p>
                        </td>
                        {/* 10. Status */}
                        <td className="py-2.5 px-2 align-middle">
                          <StatusBadge status={st} amtDue={amtDue} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 mt-3">
            {filteredInvoices.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <p className="text-sm text-gray-500">No invoices found</p>
              </div>
            ) : (
              filteredInvoices.map((item, index) => {
                const invKey = getInvoiceKey(item) || index;
                const docNum = getInvoiceDocNumber(item);
                const custName = getInvoiceCustomerName(item);
                const createdDate = getInvoiceCreatedDate(item);
                const amtDue = getInvoiceAmountDue(item);
                const invType = getInvoiceType(item);
                const st = getInvoicePaymentStatus(item);

                return (
                  <div
                    key={invKey}
                    className="border border-gray-200 rounded-xl p-4 bg-white"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D]">
                            {docNum !== "—" ? `#${docNum}` : "—"}
                          </span>
                          <TypeBadge type={invType} />
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {custName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={st} amtDue={amtDue} />
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedInvoice(item);
                            setIsViewModalOpen(true);
                          }}
                          className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Created Date</span>
                      <p className="text-xs text-gray-700">
                        {formatDate(createdDate)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Amount Due</span>
                      <p
                        className={`text-sm font-semibold ${
                          st === "paid"
                            ? "text-[#008236]"
                            : st === "partial"
                            ? "text-blue-600"
                            : "text-amber-600"
                        }`}
                      >
                        {formatCurrency(amtDue)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Invoice{" "}
                        {getInvoiceDocNumber(selectedInvoice) !== "—"
                          ? `#${getInvoiceDocNumber(selectedInvoice)}`
                          : "Details"}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {getInvoiceCustomerName(selectedInvoice)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {[
                      {
                        label: "Invoice ID / TxnID",
                        value: getInvoiceKey(selectedInvoice),
                      },
                      {
                        label: "Invoice Number",
                        value: getInvoiceDocNumber(selectedInvoice),
                      },
                      {
                        label: "Customer Name",
                        value: getInvoiceCustomerName(selectedInvoice),
                      },
                      {
                        label: "Invoice Type",
                        value: (
                          <TypeBadge
                            type={getInvoiceType(selectedInvoice)}
                          />
                        ),
                      },
                      {
                        label: "Created Date",
                        value: formatDate(
                          getInvoiceCreatedDate(selectedInvoice)
                        ),
                      },
                      {
                        label: "Due Date",
                        value: formatDate(
                          getInvoiceDueDate(selectedInvoice)
                        ),
                      },
                      {
                        label: "Ship Date",
                        value: formatDate(
                          getInvoiceShipDate(selectedInvoice)
                        ),
                      },
                      {
                        label: "Subtotal",
                        value: formatCurrency(
                          getInvoiceSubtotal(selectedInvoice)
                        ),
                      },
                      {
                        label: "Total Tax",
                        value: formatCurrency(
                          getInvoiceTotalTax(selectedInvoice)
                        ),
                      },
                      {
                        label: "Amount Due / Remaining",
                        value: formatCurrency(
                          getInvoiceAmountDue(selectedInvoice)
                        ),
                      },
                      {
                        label: "Status",
                        value: (
                          <StatusBadge
                            status={getInvoicePaymentStatus(selectedInvoice)}
                            amtDue={getInvoiceAmountDue(selectedInvoice)}
                          />
                        ),
                      },
                      ...(selectedInvoice?.raw?.BillAddress
                        ? [
                            {
                              label: "Billing Address",
                              value: formatAddress(
                                selectedInvoice.raw.BillAddress
                              ),
                            },
                          ]
                        : []),
                    ].map(({ label, value }) =>
                      value ? (
                        <div
                          key={label}
                          className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0"
                        >
                          <p className="text-xs text-gray-500 font-medium">
                            {label}
                          </p>
                          <div className="text-sm text-gray-800 font-medium text-right">
                            {value}
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                  {/* Line Items */}
                  {getInvoiceLines(selectedInvoice).length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Line Items
                      </h3>
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600">
                                Item
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600">
                                Qty
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600">
                                Rate
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600">
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {getInvoiceLines(selectedInvoice).map((l, i) => {
                              const itemName =
                                l.name ||
                                l.itemName ||
                                l.ItemRef?.FullName ||
                                l.raw?.ItemRef?.FullName ||
                                l.description ||
                                l.Desc ||
                                l.Description ||
                                "—";
                              const qty =
                                l.quantity || l.Quantity || l.Qty || 1;
                              const rate =
                                l.rate || l.Rate || l.UnitPrice || 0;
                              const amount =
                                l.amount ||
                                l.Amount ||
                                Number(qty) * Number(rate);
                              return (
                                <tr key={i} className="hover:bg-gray-50/50">
                                  <td className="px-3 py-2 text-gray-700">
                                    {itemName}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-700">
                                    {qty}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-700">
                                    {formatCurrency(rate)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-semibold text-gray-800">
                                    {formatCurrency(amount)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
